import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Bell } from "lucide-react"; // Bell ikonunu bildirim için ekledim
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";

import { IconTextButton, IconButton } from "../../components/ButtonComponents";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../hooks/useApiErrorHandler";
import { useFirmAppStore } from "../../store/firmAppStore";
import { useTableHeader } from "../../hooks/useTableHeader";
import { useDynamicScrollHeight } from "../../hooks/useDynamicScrollHeight";
import { FirmAppDialog } from "./FirmAppDialog";
import { FirmApp } from "../../types/firmApp";

export const FirmAppList = () => {
  const { data, total, loading, fetchDataDynamic: fetchApps, deleteItem, sendUpdateNotification } = useFirmAppStore();
  const { handleApiError } = useApiErrorHandler();
  const { confirm, modal: confirmModal } = useConfirmModal();
  const scrollHeight = useDynamicScrollHeight(260);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [appToEdit, setAppToEdit] = useState<FirmApp | null>(null);

  // 🟦 HEADER + SEARCH
  const { header } = useTableHeader({
    title: "Firma Uygulamaları",
    actions: <IconTextButton icon={Plus} onClick={() => { setAppToEdit(null); setIsModalOpen(true); }} className="btn-new">Yeni Ekle</IconTextButton>,
    onFilterChange: (f) => fetchApps({ filter: f ?? undefined })
  });

  useEffect(() => {
    fetchApps({ pageIndex: 1, pageSize: rows });
  }, [fetchApps, rows]);

  const handleEdit = (app: FirmApp) => {
    setAppToEdit(app);
    setIsModalOpen(true);
  };

  const handleDelete = async (app: FirmApp) => {
    if (await confirm(`${app.appName} uygulamasını silmek istiyor musunuz?`)) {
      try {
        await deleteItem(app.id);
        toast.success("Uygulama silindi");
        fetchApps({}); // Listeyi yenile
      } catch (e) {
        handleApiError(e);
      }
    }
  };

  const handleSendNotification = async (app: FirmApp) => {
    if (await confirm(`${app.appName} için güncelleme bildirimi gönderilsin mi?`)) {
      try {
        await sendUpdateNotification(app.firmId, app.appIdentifier);
        toast.success("Güncelleme bildirimi gönderildi.");
      } catch (e) {
        handleApiError(e);
      }
    }
  };

  const actionTemplate = (row: FirmApp) => (
    <div className="flex gap-2">
      <IconButton icon={Bell} onClick={() => handleSendNotification(row)} className="text-purple-600" title="Güncelleme Bildirimi Gönder" />
      <IconButton icon={Edit} onClick={() => handleEdit(row)} className="text-blue-600" title="Düzenle" />
      <IconButton icon={Trash2} onClick={() => handleDelete(row)} className="text-red-600" title="Sil" />
    </div>
  );

  return (
    <div className="w-full">
      {confirmModal}
      <DataTable 
        value={data} 
        loading={loading}
        header={header}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={total}
        onPage={(e) => {
          setFirst(e.first);
          setRows(e.rows);
          const page = Math.floor(e.first / (e.rows || 10)) + 1;
          fetchApps({ pageIndex: page, pageSize: e.rows });
        }}
        scrollable
        scrollHeight={scrollHeight}
      >
        <Column field="id" header="ID" />
        <Column field="appName" header="Uygulama Adı" />
        <Column field="appIdentifier" header="Identifier" />
        <Column field="isActive" header="Durum" body={(row) => row.isActive ? "Aktif" : "Pasif"} />
        <Column body={actionTemplate} header="İşlemler" style={{ width: "120px" }} />
      </DataTable>

      <FirmAppDialog 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        app={appToEdit} 
      />
    </div>
  );
};